/**
 * The charts the reports screen is drawn with, by hand.
 *
 * No charting library. The whole screen is a handful of small charts, and the
 * smallest library that draws them is larger than the rest of the app — a poor
 * trade on a till loading over a shop's connection. Each one is plain SVG so it
 * scales to whatever box it is given, and each carries the same figures as text
 * for anyone who cannot see it: a chart that only works visually leaves a screen
 * reader with nothing.
 *
 * The palette is fixed rather than themed — the app is one dark theme — and the
 * channel colours are shared with the cards beside the charts so a slice and its
 * card read as the same thing.
 */

// A channel's colour, shared between the donut and its card. Foodpanda keeps its
// own magenta because that is how the shop already thinks of it.
export const CHANNEL_COLORS = {
  dine_in: '#7ed957',
  takeaway: '#4bd0c9',
  delivery: '#6aa6ff',
  foodpanda: '#ef5b9c',
  phone_order: '#f5b544',
  online: '#b389f5',
};

export const CHANNEL_LABELS = {
  dine_in: 'Dining',
  takeaway: 'Takeaway',
  delivery: 'Delivery',
  foodpanda: 'Foodpanda',
  phone_order: 'Phone',
  online: 'Online',
};

const PAYMENT_COLORS = {
  cash: '#7ed957',
  card: '#6aa6ff',
  online: '#b389f5',
  account: '#f5b544',
  bank_transfer: '#4bd0c9',
  foodpanda: '#ef5b9c',
};

export function paymentColor(method, i = 0) {
  const fallback = ['#7ed957', '#6aa6ff', '#b389f5', '#f5b544', '#4bd0c9', '#ef5b9c'];
  return PAYMENT_COLORS[method] ?? fallback[i % fallback.length];
}

const PAYMENT_LABELS = {
  cash: 'Cash',
  card: 'Card',
  online: 'Online',
  account: 'On account',
  bank_transfer: 'Bank transfer',
  foodpanda: 'Foodpanda',
};

export function paymentLabel(method) {
  return PAYMENT_LABELS[method] ?? method;
}

function Empty({ note = 'Nothing to show yet.' }) {
  return <p className="py-8 text-center text-sm text-ink-3">{note}</p>;
}

/**
 * A donut, for a split that adds to a whole — which channel, which tender.
 *
 * Drawn as one ring of dashed arcs rather than pie wedges: the arithmetic is a
 * dash length, so a single slice filling the whole circle is not a special case
 * the way a full wedge would be. The total sits in the middle, because that is
 * the number the slices are shares of.
 */
export function Donut({ data, formatValue, caption, centerLabel }) {
  const items = (data ?? []).filter((d) => Number(d.value) > 0);
  const total = items.reduce((s, d) => s + Number(d.value), 0);
  if (total <= 0) return <Empty />;

  const r = 38;
  const circ = 2 * Math.PI * r;
  let acc = 0;
  const arcs = items.map((d) => {
    const frac = Number(d.value) / total;
    const len = frac * circ;
    const arc = { ...d, frac, dash: `${len} ${circ - len}`, offset: -acc };
    acc += len;
    return arc;
  });

  return (
    <figure className="m-0 flex items-center gap-4">
      <svg viewBox="0 0 100 100" className="h-32 w-32 shrink-0" role="img" aria-label={caption}>
        <g transform="rotate(-90 50 50)">
          {/* The track, so a nearly-empty donut still reads as a ring. */}
          <circle cx="50" cy="50" r={r} fill="none" stroke="#2a2a2a" strokeWidth="12" />
          {arcs.map((a, i) => (
            <circle
              key={i}
              cx="50"
              cy="50"
              r={r}
              fill="none"
              stroke={a.color}
              strokeWidth="12"
              strokeDasharray={a.dash}
              strokeDashoffset={a.offset}
            />
          ))}
        </g>
        <text
          x="50"
          y="47"
          textAnchor="middle"
          className="fill-ink"
          style={{ fontSize: '13px', fontWeight: 600 }}
        >
          {formatValue(total)}
        </text>
        {centerLabel && (
          <text x="50" y="60" textAnchor="middle" className="fill-ink-3" style={{ fontSize: '7px' }}>
            {centerLabel}
          </text>
        )}
      </svg>

      <ul className="min-w-0 flex-1 space-y-1.5">
        {arcs.map((a, i) => (
          <li key={i} className="flex items-center gap-2 text-sm">
            <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ backgroundColor: a.color }} />
            <span className="min-w-0 flex-1 truncate text-ink-2">{a.label}</span>
            <span className="shrink-0 tabular-nums text-ink-3">{Math.round(a.frac * 100)}%</span>
            <span className="w-20 shrink-0 text-right font-medium tabular-nums text-ink">
              {formatValue(Number(a.value))}
            </span>
          </li>
        ))}
      </ul>

      <figcaption className="sr-only">
        {caption}. {arcs.map((a) => `${a.label}: ${formatValue(Number(a.value))}`).join('. ')}
      </figcaption>
    </figure>
  );
}

/**
 * A column chart, for a value across time or category — takings by hour, by day.
 *
 * Labels thin out as the bars multiply, so a month does not turn its axis into a
 * smear. The tallest bar is picked out, because on this screen the question is
 * usually "when is the rush" and that is the answer.
 */
export function BarChart({
  data,
  valueOf,
  labelOf,
  formatValue,
  caption,
  labelEvery = 1,
  color = '#6bc940',
  // Pixel heights, not percentages: a percentage height resolves against the
  // parent's height, and a flex column has none to give, so the bars collapse
  // to nothing. An absolute height needs no cooperating parent.
  maxPx = 140,
  showLabels = true,
}) {
  const rows = data ?? [];
  const values = rows.map((r) => Number(valueOf(r)) || 0);
  const peak = Math.max(...values, 0);
  if (rows.length === 0 || peak <= 0) return <Empty />;
  const peakAt = values.indexOf(peak);

  return (
    <figure className="m-0">
      <div
        className="flex items-end gap-1"
        style={{ height: maxPx + (showLabels ? 16 : 0) }}
        role="img"
        aria-label={caption}
      >
        {rows.map((row, i) => {
          const value = Number(valueOf(row)) || 0;
          const height = value > 0 ? Math.max(3, (value / peak) * maxPx) : 0;
          const show = i % labelEvery === 0 || i === rows.length - 1;
          return (
            <div key={i} className="flex min-w-0 flex-1 flex-col items-center gap-1">
              <div
                className="w-full rounded-t"
                style={{
                  height: `${height}px`,
                  backgroundColor: color,
                  opacity: i === peakAt ? 1 : 0.55,
                }}
                title={`${labelOf(row)}: ${formatValue(value)}`}
              />
              {showLabels && (
                <span className="h-3 truncate text-[10px] text-ink-3">{show ? labelOf(row) : ''}</span>
              )}
            </div>
          );
        })}
      </div>
      <figcaption className="sr-only">
        {caption}. {rows.map((r) => `${labelOf(r)}: ${formatValue(Number(valueOf(r)) || 0)}`).join('. ')}
      </figcaption>
    </figure>
  );
}

/**
 * A filled line, for a run of days — the trend a week or a month traces out.
 *
 * The last point is marked, because on a trend the reader's eye goes to "where
 * did it end". A single point cannot trace a line, so a one-day range is shown
 * as a bar chart instead, upstream.
 */
export function TrendChart({ data, xOf, yOf, formatValue, caption }) {
  const rows = data ?? [];
  const ys = rows.map((r) => Number(yOf(r)) || 0);
  const peak = Math.max(...ys, 0);
  if (rows.length < 2 || peak <= 0) return <Empty />;

  const W = 600;
  const H = 160;
  const padX = 8;
  const padY = 12;
  const stepX = (W - padX * 2) / (rows.length - 1);
  const pointAt = (i, v) => ({
    x: padX + i * stepX,
    y: padY + (1 - v / peak) * (H - padY * 2),
  });
  const pts = ys.map((v, i) => pointAt(i, v));
  const line = pts.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
  const area = `${padX},${H - padY} ${line} ${W - padX},${H - padY}`;
  const last = pts[pts.length - 1];

  return (
    <figure className="m-0">
      <svg viewBox={`0 0 ${W} ${H}`} className="h-40 w-full" role="img" aria-label={caption} preserveAspectRatio="none">
        <defs>
          <linearGradient id="trendFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#6bc940" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#6bc940" stopOpacity="0" />
          </linearGradient>
        </defs>
        <polygon points={area} fill="url(#trendFill)" />
        <polyline points={line} fill="none" stroke="#7ed957" strokeWidth="2" vectorEffect="non-scaling-stroke" />
        <circle cx={last.x} cy={last.y} r="3.5" fill="#7ed957" />
      </svg>
      <figcaption className="sr-only">
        {caption}. {rows.map((r) => `${xOf(r)}: ${formatValue(Number(yOf(r)) || 0)}`).join('. ')}
      </figcaption>
    </figure>
  );
}

/**
 * Horizontal bars, for a ranked list where the length is the point — the top
 * sellers, where "how far ahead is the first" is read at a glance.
 */
export function HBars({ data, labelOf, valueOf, subOf, formatValue, color = '#6bc940', onItem }) {
  const rows = data ?? [];
  const peak = Math.max(...rows.map((r) => Number(valueOf(r)) || 0), 0);
  if (rows.length === 0 || peak <= 0) return <Empty />;

  return (
    <ol className="space-y-2">
      {rows.map((row, i) => {
        const value = Number(valueOf(row)) || 0;
        const width = Math.max(2, (value / peak) * 100);
        const clickable = typeof onItem === 'function';
        return (
          <li key={i} className="flex items-center gap-3">
            <span className="w-4 shrink-0 text-right text-xs tabular-nums text-ink-3">{i + 1}</span>
            <div
              className={`min-w-0 flex-1 ${clickable ? 'cursor-pointer rounded-md px-1 -mx-1 hover:bg-surface-3' : ''}`}
              {...(clickable
                ? { role: 'button', tabIndex: 0, onClick: () => onItem(row), onKeyDown: (e) => e.key === 'Enter' && onItem(row) }
                : {})}
            >
              <div className="mb-1 flex items-baseline justify-between gap-2">
                <span className="min-w-0 truncate text-sm text-ink">{labelOf(row)}</span>
                <span className="shrink-0 text-sm font-medium tabular-nums text-ink">
                  {formatValue(value)}
                </span>
              </div>
              <div className="h-2 w-full overflow-hidden rounded-full bg-surface-3">
                <div className="h-full rounded-full" style={{ width: `${width}%`, backgroundColor: color }} />
              </div>
            </div>
            {subOf && <span className="w-12 shrink-0 text-right text-xs tabular-nums text-ink-3">{subOf(row)}</span>}
          </li>
        );
      })}
    </ol>
  );
}
